import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Divider from 'housecall-ui/dist/DesignSystem/DataDisplay/Divider/Divider';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import FormControl from 'housecall-ui/dist/DesignSystem/Inputs/FormControls/FormControl';
import FormControlLabel from 'housecall-ui/dist/DesignSystem/Inputs/FormControls/FormControlLabel';
import Radio from 'housecall-ui/dist/DesignSystem/Inputs/RadioButtons/Radio';
import RadioGroup from 'housecall-ui/dist/DesignSystem/Inputs/RadioButtons/RadioGroup';
import Box from 'housecall-ui/dist/DesignSystem/Layout/Box/Box';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';
import type { ValueOf } from 'type-fest';
import { formatCurrency } from 'utils/currency';

import { DIALOG_PAYMENT_REVIEW, DURATION_SELECTED, fetchUpdatePlanPreview } from 'components/PayWall/Redux/Actions';
import { UNSUPPORTED } from 'components/PayWall/Redux/Data';
import {
  PayWallAdditionalUsers,
  PayWallDuration,
  PayWallFullState,
  PayWallOrgFeature,
  PayWallPlan,
  PayWallTiers,
} from 'components/PayWall/Redux/Reducer.type';
import { getSelectedDuration, getSelectedPlanV3, getTotalDueToday } from 'components/PayWall/Redux/Selectors';
import { useTrackEventsService } from 'components/Services/Analytics/useTrackEventsService';

import DowngradeTotal from './PlanSummaryTotal/DowngradeTotal';
import UnsupportedTotal from './PlanSummaryTotal/UnsupportedTotal';
import UpgradeTotal from './PlanSummaryTotal/UpgradeTotal';
import { PAY_WALL_VERSION } from './PayWallDialog';

function useIsReviewStep() {
  const currentStep = useSelector((state: PayWallFullState) => state.payWall.currentStep);
  return currentStep === DIALOG_PAYMENT_REVIEW;
}

const useStyles = makeStyles((theme: HCPTheme) => ({
  userCount: {
    color: theme.palette.secondary.main,
  },
}));

export const useBillingInfoStyles = makeStyles(() => ({
  billInfo: {
    paddingLeft: 4,
  },
  billInfoTooltip: {
    maxWidth: 250,
  },
  billInfoTooltipText: {
    fontSize: 14,
    fontWeight: 400,
    paddingTop: 10,
    paddingBottom: 10,
  },
}));

const getDurationString = (selectedDuration: ValueOf<typeof PayWallDuration> | '') => {
  let durationString = 'Monthly';

  if (selectedDuration === PayWallDuration.SixMonths) {
    durationString = 'Every 6 months';
  } else if (selectedDuration === PayWallDuration.Annual) {
    durationString = 'Annually';
  }

  return durationString;
};

function BilledSection(props: { selectedDuration: ValueOf<typeof PayWallDuration> | '' }) {
  const { selectedDuration } = props;

  const durationString = getDurationString(selectedDuration);

  return (
    <Box display="flex">
      <Typography variant="body1">{durationString}</Typography>
    </Box>
  );
}

type PriceDisplayProps = {
  price: string | React.ReactNode;
  duration: typeof PayWallDuration | string;
  strikeThrough?: boolean;
};

type PriceDisplayStyleProps = {
  strikeThrough: boolean;
};

const usePriceDisplayStyles = makeStyles(() => ({
  priceDisplay: {
    textDecoration: ({ strikeThrough }: PriceDisplayStyleProps) => (strikeThrough ? 'line-through' : 'none'),
  },
}));

export function PriceDisplay({ price, duration, strikeThrough = false }: PriceDisplayProps) {
  const classes = usePriceDisplayStyles({ strikeThrough });

  return (
    <Box display="flex" justifyContent="flex-end" alignItems="baseline" className={classes.priceDisplay}>
      <Typography variant="body1" aria-label="upsell feature price" align="right" data-testid="price-amount">
        {price}
      </Typography>
      <Box ml={0.5}>
        <Typography variant="caption" data-testid="price-cadence">
          {duration === PayWallDuration.SixMonths && 'per six months'}
          {duration === PayWallDuration.Monthly && 'per month'}
          {duration === PayWallDuration.Annual && 'per year'}
          {!Object.values(PayWallDuration).includes(duration as string) && duration}
        </Typography>
      </Box>
    </Box>
  );
}

function AdditionalUsers(props: { additionalUsers: PayWallAdditionalUsers }) {
  const { additionalUsers } = props;

  if (!additionalUsers || additionalUsers.count < 1) {
    return null;
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      aria-label="additional logins addon"
      flexWrap="nowrap"
      data-testid="additional-logins"
    >
      <Typography variant="body2">Additional logins</Typography>
      <Typography variant="body2" align="right" data-testid="count">
        {additionalUsers.count}
      </Typography>
      <PriceDisplay
        price={formatCurrency(additionalUsers.amount)}
        duration={PayWallDuration.Monthly}
        data-testid="additional-logins-amount"
      />
    </Box>
  );
}

function UpsellFeatures(props: { orgFeatures: PayWallOrgFeature[]; selectedPlan: PayWallPlan }) {
  const { orgFeatures, selectedPlan } = props;

  const features = orgFeatures
    .filter((feature) => feature.display_name && !selectedPlan.features?.includes(feature.name))
    .map(({ price, display_name: displayName, quantifiable = false, quantity = 1 }) => (
      <Box
        display="flex"
        justifyContent="space-between"
        data-testid="upsell-features"
        aria-label="upsell feature addon"
        key={`upsell_feature_${displayName}`}
      >
        <Typography variant="body2">{displayName}</Typography>
        {quantifiable && (
          <Typography variant="body2" align="right" data-testid="count">
            {quantity}
          </Typography>
        )}
        <PriceDisplay
          price={formatCurrency(price * quantity / 100.0)}
          duration={PayWallDuration.Monthly}
        />
      </Box>
    ));

  return <>{features}</>;
}

function UsersInPlan(props: { selectedPlan: PayWallPlan }) {
  const classes = useStyles();
  const { selectedPlan } = props;

  let userCount = 'Unlimited logins';

  if (selectedPlan.tier !== PayWallTiers.EXTRA_LARGE_UNLIMITED) {
    if (selectedPlan.number_of_free_seats === 1) {
      userCount = '1 login';
    } else if (selectedPlan.number_of_free_seats > 1) {
      userCount = `1-${selectedPlan.number_of_free_seats} logins`;
    }
  }

  return (
    <Typography data-testid="users-in-plan" variant="body1" className={classes.userCount}>
      {userCount}
    </Typography>
  );
}

function AddonHeader() {
  return (
    <Box>
      <Typography variant="body1">Add-ons</Typography>
    </Box>
  );
}

function Addons({ selectedPlan }: { selectedPlan: PayWallPlan | Record<string, never> }) {
  const orgFeatures = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.additional_features);
  const additionalUsers = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.additional_users);

  if (!orgFeatures?.length && !additionalUsers?.count) {
    return null;
  }

  return (
    <Box data-testid="addons-container">
      <AddonHeader />
      <AdditionalUsers additionalUsers={additionalUsers} />
      <UpsellFeatures selectedPlan={selectedPlan} orgFeatures={orgFeatures} />
      <Box my={2}>
        <Divider />
      </Box>
    </Box>
  );
}

function Total(props: { whenToApplyChange: string; totalDueToday: number }) {
  const { whenToApplyChange, totalDueToday } = props;

  if (totalDueToday > 0) {
    return <UpgradeTotal />;
  }

  if (whenToApplyChange === UNSUPPORTED) {
    return <UnsupportedTotal />;
  }

  return <DowngradeTotal />;
}

export default function PayWallPlanSummary(): JSX.Element {
  const { trackEvent } = useTrackEventsService();
  const planAmount = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.plan_amount);
  const selectedDuration = useSelector(getSelectedDuration);
  const whenToApplyChange = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.when_to_apply);
  const selectedPlan = useSelector(getSelectedPlanV3);
  const totalDueToday = useSelector(getTotalDueToday);
  const dispatch = useDispatch();
  const isReview = useIsReviewStep();

  return (
    <Box>
      <Box mb={1} display="flex" justifyContent="space-between">
        <Typography variant="h3">Price</Typography>
      </Box>

      <Box mb={2}>
        <Typography variant="body1">{selectedPlan.display_name}</Typography>
        <UsersInPlan selectedPlan={selectedPlan} />
      </Box>

      <Box mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            {!isReview && (
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  onChange={(_, duration) => {
                    dispatch({ type: DURATION_SELECTED, duration });
                    dispatch(fetchUpdatePlanPreview({ planType: duration }));
                    trackEvent('Plan duration switched', { planType: duration, payWallVersion: PAY_WALL_VERSION });
                  }}
                  aria-label="plan duration"
                  data-testid="radio-cadence"
                  name="planDuration"
                  value={selectedDuration}
                >
                  <FormControlLabel
                    value={PayWallDuration.Monthly}
                    control={<Radio color="primary" data-testid="radio-cadence-monthly" />}
                    label={getDurationString(PayWallDuration.Monthly)}
                  />
                  <FormControlLabel
                    value={PayWallDuration.Annual}
                    control={<Radio color="primary" data-testid="radio-cadence-annual" />}
                    label={getDurationString(PayWallDuration.Annual)}
                  />
                </RadioGroup>
              </FormControl>
            )}
            {isReview && <BilledSection selectedDuration={selectedDuration} />}
          </Box>
          <Box data-testid="plan-base-price">
            <PriceDisplay price={formatCurrency(planAmount)} duration={PayWallDuration.Monthly} />
            {selectedPlan.discount_label && (
              <Typography data-testid="partner-discount">
                Includes a ${selectedPlan.discount_label} preferred partner discount.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box my={2}>
        <Divider />
      </Box>

      <Addons selectedPlan={selectedPlan} />

      <Total whenToApplyChange={whenToApplyChange} totalDueToday={Number(totalDueToday)} />
    </Box>
  );
}
